import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { ArrowRight, Stars02, X } from '@untitledui/icons';
import { Avatar } from '@/components/base/avatar/avatar';
import { Badge } from '@/components/base/badges/badges';
import type { Match } from '@/entities/match';
import { nameInitials } from '@/features/matches/lib/initials';

export type MatchDetailsDrawerProps = {
  match: Match | null;
  onClose: () => void;
  onAccept?: (matchId: string) => void;
  onSkip?: (matchId: string) => void;
  isAccepting?: boolean;
};

export const MatchDetailsDrawer = ({
  match,
  onClose,
  onAccept,
  onSkip,
  isAccepting,
}: MatchDetailsDrawerProps) => {
  const open = match !== null;

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!match) return null;

  const { participante, score, altoPotencial, motivos, status } = match;
  const isInConversation = status === 'ACEITO' || status === 'CONVERSANDO';

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        {/* Em mobile o conteudo eh full-screen (sem overlay visivel); em
            desktop o overlay escurece o fundo do drawer lateral. */}
        <Dialog.Overlay className="fixed inset-0 z-40 hidden bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out md:block" />
        <Dialog.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-50 flex flex-col bg-white outline-none data-[state=open]:animate-in data-[state=open]:fade-in data-[state=closed]:animate-out data-[state=closed]:fade-out md:inset-y-0 md:left-auto md:right-0 md:w-[480px] md:border-l md:border-gray-100 md:shadow-2xl md:data-[state=open]:slide-in-from-right md:data-[state=closed]:slide-out-to-right"
        >
          {/* Header sticky com X tappable (40x40 touch target). */}
          <header
            className="sticky top-0 z-10 flex items-center justify-between gap-3 border-b border-gray-100 bg-white px-4 py-3"
            style={{ paddingTop: 'max(12px, env(safe-area-inset-top))' }}
          >
            <Dialog.Title className="text-base font-semibold tracking-tight text-gray-900">
              Detalhes
            </Dialog.Title>
            <Dialog.Close
              aria-label="Fechar"
              className="flex h-10 w-10 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 active:bg-gray-200"
            >
              <X className="size-6" aria-hidden="true" />
            </Dialog.Close>
          </header>

          <div className="flex-1 overflow-y-auto">
            {/* Bloco do header do perfil dentro do scrollable. */}
            <section className="flex items-start gap-4 border-b border-gray-100 px-5 py-5">
              <Avatar
                size="xl"
                initials={nameInitials(participante.nome)}
                src={participante.fotoUrl ?? undefined}
                alt={participante.nome}
              />
              <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                <p className="truncate text-xl font-bold tracking-tight text-gray-900">
                  {participante.nome}
                </p>
                <p className="truncate text-sm text-gray-500">
                  {[participante.cargo, participante.empresa].filter(Boolean).join(' · ') ||
                    'Sem cargo informado'}
                </p>
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  <Badge
                    size="md"
                    color={altoPotencial ? 'success' : 'gray'}
                    type={altoPotencial ? 'pill-color' : 'color'}
                  >
                    {altoPotencial ? (
                      <span className="inline-flex items-center gap-1">
                        <Stars02 className="size-3" aria-hidden="true" />
                        Score {score} · Alto potencial
                      </span>
                    ) : (
                      <span>Score {score}</span>
                    )}
                  </Badge>
                </div>
              </div>
            </section>

            <div className="px-5 py-6">
            <div className="flex flex-col gap-6">
              {participante.bio ? (
                <section className="flex flex-col gap-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Sobre
                  </h4>
                  <p className="text-sm leading-relaxed text-gray-700">{participante.bio}</p>
                </section>
              ) : null}

              {(participante.tagsBusca?.length ?? 0) > 0 && (
                <section className="flex flex-col gap-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Busca
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {participante.tagsBusca?.map((t) => (
                      <Badge key={t.id} size="md" color="blue" type="color">
                        {t.nome}
                      </Badge>
                    ))}
                  </div>
                </section>
              )}

              {(participante.tagsOferta?.length ?? 0) > 0 && (
                <section className="flex flex-col gap-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Oferece
                  </h4>
                  <div className="flex flex-wrap gap-1.5">
                    {participante.tagsOferta?.map((t) => (
                      <Badge key={t.id} size="md" color="purple" type="color">
                        {t.nome}
                      </Badge>
                    ))}
                  </div>
                </section>
              )}

              {motivos.length > 0 && (
                <section className="flex flex-col gap-2">
                  <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-500">
                    Por que combinam
                  </h4>
                  <ul className="flex flex-col gap-2 rounded-xl bg-gray-50 px-4 py-3">
                    {motivos.map((m) => (
                      <li key={m} className="flex items-start gap-2 text-sm text-gray-700">
                        <span className="mt-2 size-1 shrink-0 rounded-full bg-gray-400" />
                        {m}
                      </li>
                    ))}
                  </ul>
                </section>
              )}
            </div>
            </div>
          </div>

          <footer
            className="sticky bottom-0 z-10 border-t border-gray-100 bg-white px-4 py-3"
            style={{ paddingBottom: 'max(12px, env(safe-area-inset-bottom))' }}
          >
            {isInConversation ? (
              <Link
                to={`/app/chat/${match.id}`}
                onClick={onClose}
                className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-full bg-black px-4 text-sm font-semibold text-white transition hover:bg-gray-900"
              >
                Ir para o chat
                <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            ) : (
              <div className="flex items-center gap-2">
                {onSkip ? (
                  <button
                    type="button"
                    onClick={() => onSkip(match.id)}
                    className="inline-flex h-14 flex-1 items-center justify-center rounded-full border border-gray-200 px-4 text-sm font-semibold text-gray-700 transition hover:bg-gray-50"
                  >
                    Pular
                  </button>
                ) : null}
                {onAccept ? (
                  <button
                    type="button"
                    onClick={() => onAccept(match.id)}
                    disabled={isAccepting}
                    className="inline-flex h-14 flex-[2] items-center justify-center rounded-full bg-black px-4 text-sm font-semibold text-white transition hover:bg-gray-900 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {isAccepting ? 'Enviando…' : 'Quero conversar'}
                  </button>
                ) : null}
              </div>
            )}
          </footer>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
